import { useEffect, useRef } from 'react';
import Fade from '@mui/material/Fade';
import Typography from "@mui/material/Typography";
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { useMediaQuery, useTheme } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import CustomLink from './CustomLink';
import { useAssignedVideos } from './AssignedVideosContext';
import { useAuth } from './AuthContext';
import { api } from '../api';
import { ACTIVITY_TYPES } from '../api/constants';

// Scroll depth milestones (percent) logged for the CDC guidelines PDF
const SCROLL_DEPTH_MILESTONES = [25, 50, 75, 100];

const VaccineInfo = () => {
    const { t, i18n } = useTranslation();
    const { videosCount } = useAssignedVideos();
    const { isAdmin, isLoggedIn, authLoading } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const currentLng = i18n.language;
    const PDF_URL_EN = process.env.REACT_APP_PDF_URL_EN;
    const PDF_URL_VI = process.env.REACT_APP_PDF_URL_VI;
    const pdfUrl = currentLng === 'vi' ? PDF_URL_VI : PDF_URL_EN;
    const localPdfPath = currentLng === 'vi' ? '/vietnamese_hpv.pdf' : '/english_hpv.pdf';
    const viewerUrl = `/pdfjs/web/viewer.html?file=${encodeURIComponent(localPdfPath)}`;
    const visitLoggedRef = useRef(false);
    const iframeRef = useRef(null);
    const loggedMilestonesRef = useRef(new Set());
    const isParticipant = !authLoading && isLoggedIn && !isAdmin;

    useEffect(() => {
        // Wait for auth to resolve
        if (!isParticipant) return;
        if (visitLoggedRef.current) return;
        visitLoggedRef.current = true;
        api.post('/activity/create', { activityType: ACTIVITY_TYPES.USER_VISITED_VACCINE_INFO_PAGE })
            .catch((err) => console.error('Error creating activity log:', err));
    }, [isParticipant]);

    // Track scroll depth inside the PDF viewer iframe
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe || !isParticipant) return;

        let scrollContainer = null;

        const handleScroll = () => {
            // Ignore events before the PDF pages have rendered
            if (!scrollContainer || scrollContainer.scrollHeight <= scrollContainer.clientHeight) {
                return;
            }
            const depth = ((scrollContainer.scrollTop + scrollContainer.clientHeight) / scrollContainer.scrollHeight) * 100;
            for (const milestone of SCROLL_DEPTH_MILESTONES) {
                if (depth >= milestone && !loggedMilestonesRef.current.has(milestone)) {
                    loggedMilestonesRef.current.add(milestone);
                    api.post('/activity/create', {
                        activityType: ACTIVITY_TYPES.USER_SCROLLED_VACCINE_INFO_PDF,
                        scrollDepth: milestone,
                    }).catch((err) => console.error('Error creating activity log:', err));
                }
            }
        };

        const handleIframeLoad = () => {
            // Same-origin, so the viewer's scroll container is reachable
            scrollContainer = iframe.contentDocument?.getElementById('viewerContainer');
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
            }
        };

        iframe.addEventListener('load', handleIframeLoad);
        // The iframe may have finished loading before this effect ran
        if (iframe.contentDocument?.readyState === 'complete') {
            handleIframeLoad();
        }

        return () => {
            iframe.removeEventListener('load', handleIframeLoad);
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
        };
    }, [isParticipant, viewerUrl]);

    // Log clicks on the PDF download link (the only PDF signal available on
    // mobile, where the inline viewer is not rendered)
    const handlePdfLinkClick = () => {
        if (!isParticipant) return;
        api.post('/activity/create', { activityType: ACTIVITY_TYPES.USER_OPENED_VACCINE_INFO_PDF })
            .catch((err) => console.error('Error creating activity log:', err));
    };

    return (
        <Fade in timeout={1000}>
            <div className="main">
                <Box sx={{ width: { xs: '100%', sm: '90%', md: '80%' }, margin: 'auto', padding: { xs: 2, sm: 4 } }}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: '600', textAlign: 'center' }}>{t("vaccine_info.title")}</Typography>
                    <Paper elevation={3} sx={{ padding: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
                        <Typography variant="body1" sx={{ mb: '1rem' }}>{isMobile ? t("vaccine_info.paragraph_1_mobile") : t("vaccine_info.paragraph_1")}</Typography>
                        {pdfUrl && (
                            <Link to={pdfUrl} target="_blank" rel="noopener noreferrer" onClick={handlePdfLinkClick}>
                                {t("vaccine_info.download_pdf")}
                                <OpenInNewIcon sx={{ fontSize: 'medium', ml: 0.5, verticalAlign: 'middle' }} />
                            </Link>
                        )}
                    </Paper>
                    {!isMobile && (
                        <iframe ref={iframeRef} src={viewerUrl} width="100%" height="700px" style={{ border: 'none', maxHeight: '95vh' }} title='Vaccine Information Statement'></iframe>
                    )}
                    <Paper elevation={3} sx={{ padding: 2, mt: 4, mb: 4, borderLeft: '7px solid rgb(91, 59, 140)', wordBreak: 'break-word' }}>
                        <Typography variant="body1" sx={{ display: 'inline', mr: 1 }}>{t("vaccine_info.paragraph_2")}</Typography>
                        <Link to={t("vaccine_info.info_link")} target="_blank" rel="noopener noreferrer">{t("vaccine_info.info_link")}</Link>
                    </Paper>
                    { videosCount > 0 && <CustomLink to="/videos" linkText={t("vaccine_info.paragraph_3")} /> }
                </Box>
                
            </div>
        </Fade>
    )
}

export default VaccineInfo;